import { useState } from 'react';
import { Link } from 'react-router';
import { getRemembered } from '@/ble/registry';
import { useDevices } from '@/store/devices';
import { useSettings } from '@/store/settings';
import { needsFtp } from '@/store/settingsModel';
import { useT } from '@/ui/i18n';

const DISMISS_KEY = 'velostube.firstStepsDismissed';

function dismissed(): boolean {
  try {
    return localStorage.getItem(DISMISS_KEY) === '1';
  } catch {
    return false;
  }
}

/** First-run setup strip on the home page until the first ride is saved (or it is dismissed). */
export function FirstStepsCard({ hasRides }: { hasRides: boolean }) {
  const t = useT();
  const [hidden, setHidden] = useState(dismissed);
  const ftpSet = useSettings((s) => !needsFtp(s.settings));
  const trainerKnown = useDevices((s) => s.trainer.state === 'connected') || !!getRemembered('trainer');
  if (hidden || hasRides) return null;
  const steps = [
    { done: ftpSet, to: '/einstellungen', label: t('first.settings') },
    { done: trainerKnown, to: '/geraete', label: t('first.trainer') },
    { done: false, to: '/fahren', label: t('first.ride') },
  ];
  const done = steps.filter((s) => s.done).length;
  return (
    <section
      className="card grid"
      style={{ gap: 14 }}
      data-testid="first-steps"
      aria-labelledby="first-title"
    >
      <div className="spread">
        <h2 id="first-title" style={{ margin: 0, fontSize: 16 }}>
          {t('first.title')}{' '}
          <span className="dim num">
            · {done} / {steps.length}
          </span>
        </h2>
        <button
          className="ghost small"
          onClick={() => {
            try {
              localStorage.setItem(DISMISS_KEY, '1');
            } catch {
              /* ignore */
            }
            setHidden(true);
          }}
        >
          {t('first.dismiss')}
        </button>
      </div>
      <div className="progress" aria-hidden>
        <span style={{ width: `${(done / steps.length) * 100}%` }} />
      </div>
      <ol className="setup-steps">
        {steps.map((s) => (
          <li key={s.to} className={s.done ? 'done' : ''}>
            <span className="tick" aria-hidden />
            <Link to={s.to}>{s.label}</Link>
          </li>
        ))}
      </ol>
      <p className="dim small" style={{ margin: 0 }}>
        {t('first.hint')}
      </p>
    </section>
  );
}
